import { UTCDate } from '@date-fns/utc';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useGetStageMapQuery } from '../../../../services/data/impl/wrangler/endpoints/blueberry';
import { StageMapRequest } from '../../../../services/data/impl/wrangler/schemas/stageMapRequest';
import { StageMapResponseRow } from '../../../../services/data/impl/wrangler/schemas/stageMapResponse';
import { Stage } from '../../../../services/entities/impl/blfApi/schemas/stage';
import {
  TableColumnDefinitions,
  useLoadTable,
} from '../../../duckDB/hooks/useLoadTable';

export const useLoadBlockStageMap = (
  customerId: string | undefined,
  blockId: string | undefined,
  scanDate: string | undefined,
  stages: Stage[] | undefined,
  skip?: boolean
): { loading: boolean } => {
  const requestBody = useMemo<StageMapRequest | typeof skipToken>(() => {
    if (
      customerId &&
      blockId &&
      scanDate &&
      stages &&
      stages.length > 0 &&
      !skip
    ) {
      return {
        meta: {},
        data: {
          customer_id: customerId,
          scan_date: new UTCDate(scanDate).toISOString(),
          block_id: blockId,
          stage_definitions: stages.map((stage) => ({
            stage_id: stage.id,
            stage_name: stage.name,
            filters: {
              class_names: [stage.className],
              color_sets: stage.colorSets || [],
              sub_classes: stage.subClasses || [],
            },
          })),
        },
      };
    }
    return skipToken;
  }, [customerId, blockId, scanDate, stages, skip]);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetStageMapQuery(requestBody ?? skipToken);

  const { isTableLoading } = useLoadTable<StageMapResponseRow>(
    'stage_map_table',
    response,
    {
      columns: STAGE_MAP_COLUMN_DEFS,
      fileType: 'json',
    }
  );

  return {
    loading: isTableLoading || isLoading || isFetching,
  };
};

const STAGE_MAP_COLUMN_DEFS: TableColumnDefinitions = {
  point_id: 'VARCHAR',
  alley: 'VARCHAR',
  block_id: 'VARCHAR',
  block_name: 'VARCHAR',
  customer_id: 'VARCHAR',
  customer_short_code: 'VARCHAR',
  gps: 'VARCHAR',
  left_camera_img_path: 'VARCHAR',
  right_camera_img_path: 'VARCHAR',
  ranch_id: 'VARCHAR',
  ranch_name: 'VARCHAR',
  row_id: 'VARCHAR',
  rowside_id: 'VARCHAR',
  scan_date: 'TIMESTAMP_S',
  scan_id: 'VARCHAR',
  stage_count: 'FLOAT',
  stage_id: 'VARCHAR',
  view_direction: 'VARCHAR',
};
